import type { FeedbackReaction } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { track as TrackFunction } from "pro-features/components/WithTrack/src/WithTrack";
import type { CustomAttributes } from "app/services/track/track";

const createAIEmailAgentTrackingService = (track: TrackFunction) => {
    const trackEmailEvent = (action: string, eventName: string, customAttributes?: CustomAttributes): void => {
        track("craft email agent", action, eventName, customAttributes);
    };

    const trackSelectCompany = (company: string) => {
        const eventName = createTrackingEventName("select company", company);
        trackEmailEvent("click", eventName);
    };

    const trackGenerateEmail = () => {
        trackEmailEvent("click", "generate email");
    };

    const trackSubmitFeedback = (company: string, reaction: FeedbackReaction) => {
        const eventName = createTrackingEventName(`${reaction} brief`, company);
        trackEmailEvent("click", eventName);
    };

    const trackCopyText = (company: string) => {
        const eventName = createTrackingEventName("copy brief", company);
        trackEmailEvent("click", eventName);
    };

    const trackManualCopyText = (company: string) => {
        const eventName = createTrackingEventName("manual copy", company);
        trackEmailEvent("click", eventName);
    };

    const trackEmailSubjectEdit = () => {
        const eventName = createTrackingEventName("edit email", "subject line");
        trackEmailEvent("click", eventName);
    };

    const trackEmailBodyEdit = () => {
        const eventName = createTrackingEventName("edit email", "body");
        trackEmailEvent("click", eventName);
    };

    const trackSelectRecipient = (contact: string, isSuggested?: boolean) => {
        const eventName = createTrackingEventName(
            "recipient selection",
            isSuggested ? "suggestions" : "search results",
            contact,
        );
        trackEmailEvent("click", eventName);
    };

    const trackAddNewRecipient = (contact: string) => {
        const eventName = createTrackingEventName("recipient selection", "add as new", contact);
        trackEmailEvent("click", eventName);
    };

    const trackSelectVisual = (insight: string) => {
        const eventName = createTrackingEventName("select insight", insight, "visual");
        trackEmailEvent("click", eventName);
    };

    const trackRevertVersion = () => {
        const eventName = createTrackingEventName("revert");
        trackEmailEvent("click", eventName);
    };

    const trackSendMessage = (message: string) => {
        const eventName = createTrackingEventName("send prompt", message, message.length);
        trackEmailEvent("click", eventName);
    };

    const trackPauseAgent = () => {
        const eventName = createTrackingEventName("pause agent");
        trackEmailEvent("click", eventName);
    };

    const trackApplyVisualSuggestion = () => {
        const eventName = createTrackingEventName("prompt suggestions", "change insight");
        trackEmailEvent("click", eventName);
    };

    const trackApplyCompetitorSuggestion = () => {
        const eventName = createTrackingEventName("prompt suggestions", "change competitors");
        trackEmailEvent("click", eventName);
    };

    const trackApplyCompetitors = (competitorsCount: number) => {
        const eventName = createTrackingEventName("competitors change", competitorsCount);
        trackEmailEvent("click", eventName);
    };

    const trackRemoveCompetitor = (competitor: string) => {
        const eventName = createTrackingEventName("remove competitor", competitor);
        trackEmailEvent("click", eventName);
    };

    const trackAddCompetitor = (competitor: string) => {
        const eventName = createTrackingEventName("add competitor", competitor);
        trackEmailEvent("click", eventName);
    };

    const trackGmailCTA = (hasExtension: boolean) => {
        const eventName = createTrackingEventName("Gmail CTA", hasExtension ? "Extension user" : "Non-extension user");
        trackEmailEvent("click", eventName);
    };

    return {
        trackSelectCompany,
        trackGenerateEmail,
        trackSubmitFeedback,
        trackCopyText,
        trackManualCopyText,
        trackEmailBodyEdit,
        trackEmailSubjectEdit,
        trackSelectRecipient,
        trackAddNewRecipient,
        trackSelectVisual,
        trackRevertVersion,
        trackSendMessage,
        trackPauseAgent,
        trackApplyVisualSuggestion,
        trackApplyCompetitorSuggestion,
        trackApplyCompetitors,
        trackRemoveCompetitor,
        trackAddCompetitor,
        trackGmailCTA,
    };
};

export default createAIEmailAgentTrackingService;
